import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bufferSource, fromBase64, toBase64, utf8 } from './bytes';
import { KEY_BYTES, open, seal } from './crypto';
import type { PassphraseParams } from './passphraseGuard';
import { ARGON2_PARAMS, derivePassphraseSecret, newPassphraseSalt } from './passphraseGuard';
import type { PrfBinding } from './webauthnPrf';
import { enrolPrf, evaluatePrf } from './webauthnPrf';
import type { VaultSession } from '../session/vaultSession';

/**
 * Remembering this browser: what sits on disk between sessions, and what it
 * takes to open it.
 *
 * The vault key is never stored. What is stored is the key SEALED under a wrap
 * key this browser cannot assemble alone, and it takes THREE things:
 *
 *  - a **non-extractable AES-GCM key** in IndexedDB, which no script can export
 *    — so a storage dump lifted through JavaScript is inert;
 *  - a **share held by the backend**, released only while the grant is live —
 *    so revoking from the phone leaves what is here permanently dead rather
 *    than merely refused;
 *  - a **guard on this machine**: a passkey's PRF answer where there is a
 *    platform authenticator, and otherwise a passphrase. Never nothing.
 *
 * The guard is what stands between a copied profile and the vault, which is why
 * there is no third mode without one. The local key is not credited with that
 * job: the W3C spec is explicit that non-extractable key material may still be
 * written to disk unencrypted, so it covers the JavaScript dump and no more.
 *
 * Chicken-and-egg, resolved: the trust id is what buys the share, and the grant
 * secret is inside what the share opens. So the id is sealed under the local key
 * ALONE — openable offline, useless by itself — and everything that reaches the
 * vault sits behind all three.
 */

const DB_NAME = 'pondering-trust';
const DB_VERSION = 1;
const STORE = 'browser';
const RECORD = 'record';
const WRAP_KEY = 'wrapKey';

const WRAP_INFO = 'pondering.trustedBrowser.v1';

/**
 * What holds the stored key on this machine. The word the UI shows and the word
 * the phone lists, so both say the same thing.
 *
 * `none` is a real choice, not a failure: on a machine with no platform
 * authenticator, requiring a phrase means someone types one every cold load to
 * protect against an attacker their disk encryption already covers. It is
 * offered rather than defaulted to, and it is never silent — the unlock screen,
 * the palette and the phone's session list all name it.
 */
export type GuardKind = 'hardware' | 'passphrase' | 'none';

interface Cell {
  iv: Uint8Array;
  ct: Uint8Array;
}

/** Openable BEFORE anything is unlocked, on purpose: a browser has to know which
 *  guard to ask for. Neither shape carries a secret — a credential id names a
 *  passkey it cannot use, and a salt is not a phrase. */
type StoredGuard =
  | { kind: 'hardware'; credentialId: Uint8Array; salt: Uint8Array }
  | ({ kind: 'passphrase'; salt: Uint8Array; kdf: 'argon2id' } & PassphraseParams)
  | { kind: 'none' };

interface StoredTrust {
  v: 2;
  /** The trust id, under the local key only — see the file comment. */
  unlock: Cell;
  /** dataKey + grantSecret, under HKDF(share ‖ guard) and then the local key. */
  keys: Cell;
  guard: StoredGuard;
}

/** What the caller asks for. Hardware falls back by ANSWERING, never by quietly
 *  sealing something weaker: an unsupported machine comes back needing a phrase,
 *  and only an explicit `none` seals without a guard. */
export type GuardChoice =
  | { kind: 'hardware' }
  | { kind: 'passphrase'; phrase: string }
  | { kind: 'none' };

export type RememberOutcome =
  | { kind: 'remembered'; guard: GuardKind }
  /** The custodian did not allow this browser to persist, or the grant died. */
  | { kind: 'refused' }
  /** No platform authenticator here, so the caller has to collect a phrase and
   *  come back. Nothing was stored and no share was minted. */
  | { kind: 'needs-passphrase' }
  /** The person declined the fingerprint on a machine that offered one. Taken at
   *  face value: nothing is stored, so this stays a session-only browser. */
  | { kind: 'declined' }
  | { kind: 'unavailable' };

export type UnlockOutcome =
  /** Nothing stored here; the ordinary first visit. */
  | { kind: 'none' }
  | { kind: 'opened'; session: VaultSession; guard: GuardKind }
  /** Sealed under a phrase. Ask for it and call again. */
  | { kind: 'needs-passphrase' }
  /** Wrong phrase — retryable, and nothing has been dropped. */
  | { kind: 'wrong-passphrase' }
  /** The backend refused the share: revoked, expired, or another account. What
   *  was stored has been dropped, because it can never open again. */
  | { kind: 'refused' }
  /** User declined the fingerprint, or the network is down. Nothing dropped. */
  | { kind: 'deferred' }
  /** Stored but unopenable — a cleared key store, a half-written record. */
  | { kind: 'unusable' };

/**
 * Where the record and the local key live. Injectable so the sealing can be
 * tested on a platform with no IndexedDB — and so a test can look at exactly
 * what lands at rest, which is the only way to check that no key does.
 */
export interface TrustStore {
  get<T>(key: string): Promise<T | null>;
  put(entries: [string, unknown][]): Promise<boolean>;
  clear(): Promise<void>;
}

export function mapTrustStore(): TrustStore {
  const cells = new Map<string, unknown>();
  return {
    get: async <T>(key: string) => (cells.get(key) as T) ?? null,
    put: async (entries) => {
      for (const [key, value] of entries) cells.set(key, value);
      return true;
    },
    clear: async () => cells.clear(),
  };
}

/** Answers null rather than throwing where a browser has no IndexedDB at all
 *  (private mode in some engines), which reads as "not remembered". */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      request.onsuccess = () => {
        // So this connection never becomes the one that blocks a later version:
        // an open handle from an old build is what hung the read cache.
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export const indexedDbTrustStore: TrustStore = {
  async get<T>(key: string): Promise<T | null> {
    const db = await openDb();
    if (!db) return null;
    return new Promise((resolve) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      request.onsuccess = () => resolve((request.result as T) ?? null);
      request.onerror = () => resolve(null);
    });
  },

  async put(entries: [string, unknown][]): Promise<boolean> {
    const db = await openDb();
    if (!db) return false;
    return new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      for (const [key, value] of entries) store.put(value, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    });
  },

  async clear(): Promise<void> {
    const db = await openDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  },
};

/** Generated here and never exported. Its whole job is to make an IndexedDB
 *  value that JavaScript alone cannot turn back into bytes. */
async function newLocalKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

async function lock(key: CryptoKey, plaintext: Uint8Array): Promise<Cell> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bufferSource(plaintext)),
  );
  return { iv, ct };
}

async function unlockCell(key: CryptoKey, cell: Cell): Promise<Uint8Array | null> {
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: bufferSource(cell.iv) },
        key,
        bufferSource(cell.ct),
      ),
    );
  } catch {
    return null;
  }
}

/**
 * The wrap key proper: the share, and whatever the guard contributes.
 *
 * An unguarded record contributes nothing, so the key is HKDF over the share
 * alone — still useless without the backend, which is what keeps revocation
 * working in every mode. The lengths cannot be read two ways: a share is always
 * 32 bytes and a guard's answer is 32 or none.
 */
function wrapKey(share: Uint8Array, guardSecret: Uint8Array): Uint8Array {
  const ikm = new Uint8Array(share.length + guardSecret.length);
  ikm.set(share, 0);
  ikm.set(guardSecret, share.length);
  return hkdf(sha256, ikm, new Uint8Array(32), utf8(WRAP_INFO), KEY_BYTES);
}

const NO_GUARD_SECRET = new Uint8Array(0);

/** The stored guard's answer, or null when this machine cannot produce it right
 *  now — a declined prompt, a passkey that has gone, a phrase not given. */
async function answerGuard(guard: StoredGuard, phrase: string | undefined): Promise<Uint8Array | null> {
  if (guard.kind === 'none') return NO_GUARD_SECRET;
  if (guard.kind === 'hardware') return evaluatePrf(guard as PrfBinding);
  if (phrase === undefined) return null;
  return derivePassphraseSecret(phrase, guard.salt, { t: guard.t, m: guard.m, p: guard.p });
}

/** Binds the sealed keys to the trust they were minted with, so a blob cannot
 *  be opened with a share issued for some other browser. */
const keysAad = (trustId: string): Uint8Array => utf8(`${WRAP_INFO}:${trustId}`);

export async function hasRememberedBrowser(store: TrustStore = indexedDbTrustStore): Promise<boolean> {
  return (await readRecord(store)) !== null;
}

/** Which guard a stored record will ask for, or null when there is none. Read
 *  before unlocking, because the surface has to know what to put on screen. */
export async function rememberedGuard(store: TrustStore = indexedDbTrustStore): Promise<GuardKind | null> {
  return (await readRecord(store))?.guard.kind ?? null;
}

/** A version this build does not understand is not a record. Nothing is wiped:
 *  the next remember replaces it, and guessing at an unknown shape is worse. */
async function readRecord(store: TrustStore): Promise<StoredTrust | null> {
  const record = await store.get<StoredTrust>(RECORD);
  return record?.v === 2 && record.guard ? record : null;
}

/**
 * Seals this session for a later visit, under the guard the caller asked for.
 *
 * `mint` asks the backend for a trust id and share; it must answer null when the
 * grant is not allowed to persist, which is the custodian's decision and the
 * reason this cannot be a purely local act. It is called only once a guard is in
 * hand, so an abandoned prompt leaves no share behind to clean up.
 */
export async function rememberThisBrowser(
  session: VaultSession,
  accountLabel: string,
  choice: GuardChoice,
  mint: () => Promise<{ trustId: string; share: string } | null>,
  store: TrustStore = indexedDbTrustStore,
): Promise<RememberOutcome> {
  if (!crypto.subtle) return { kind: 'unavailable' };

  let guard: StoredGuard;
  let guardSecret: Uint8Array;
  if (choice.kind === 'hardware') {
    const enrolment = await enrolPrf(accountLabel);
    // Declining is an answer about this machine, so it is honoured rather than
    // downgraded; an unsupported one asks for the other guard instead.
    if (enrolment.status === 'declined') return { kind: 'declined' };
    if (enrolment.status === 'unsupported') return { kind: 'needs-passphrase' };
    guard = { kind: 'hardware', ...enrolment.binding };
    guardSecret = enrolment.secret;
  } else if (choice.kind === 'none') {
    guard = { kind: 'none' };
    guardSecret = NO_GUARD_SECRET;
  } else {
    const salt = newPassphraseSalt();
    guard = { kind: 'passphrase', salt, kdf: 'argon2id', ...ARGON2_PARAMS };
    guardSecret = derivePassphraseSecret(choice.phrase, salt);
  }

  const minted = await mint();
  if (!minted) return { kind: 'refused' };
  // Fixed-length, so `share ‖ guard` cannot be read two ways: a 64-byte "share"
  // would derive the same wrap key as a 32-byte one plus a guard's answer.
  const share = decodeKey(minted.share);
  if (!share) return { kind: 'unavailable' };

  const localKey = await newLocalKey();
  const payload = utf8(
    JSON.stringify({
      dataKey: toBase64(session.dataKey),
      grantSecret: toBase64(session.grantSecret),
    }),
  );
  const sealed = seal(wrapKey(share, guardSecret), keysAad(minted.trustId), payload);

  const record: StoredTrust = {
    v: 2,
    unlock: await lock(localKey, utf8(minted.trustId)),
    keys: await lock(localKey, sealed),
    guard,
  };

  const stored = await store.put([
    [WRAP_KEY, localKey],
    [RECORD, record],
  ]);
  if (!stored) return { kind: 'unavailable' };
  return { kind: 'remembered', guard: guard.kind };
}

/**
 * Opens what a previous session stored. `fetchShare` must answer null ONLY on a
 * definitive refusal — a revoked or expired grant — and throw on anything
 * transient, because this drops the stored blob on a refusal and keeps it on a
 * failure.
 */
export async function openRememberedBrowser(
  fetchShare: (trustId: string) => Promise<string | null>,
  store: TrustStore = indexedDbTrustStore,
  phrase?: string,
): Promise<UnlockOutcome> {
  const wipe = () => store.clear();
  const record = await readRecord(store);
  if (!record) return { kind: 'none' };
  // Asked for before the share, so a phrase-guarded browser does not spend a
  // backend call every time the form is shown.
  if (record.guard.kind === 'passphrase' && phrase === undefined) return { kind: 'needs-passphrase' };

  const localKey = await store.get<CryptoKey>(WRAP_KEY);
  if (!localKey) {
    // The key store went and the ciphertext stayed: nothing here will ever open.
    await wipe();
    return { kind: 'unusable' };
  }

  const idBytes = await unlockCell(localKey, record.unlock);
  if (!idBytes) {
    await wipe();
    return { kind: 'unusable' };
  }
  const trustId = new TextDecoder().decode(idBytes);

  let share: string | null;
  try {
    share = await fetchShare(trustId);
  } catch {
    // Offline, or a bad minute at the backend. The blob is still good.
    return { kind: 'deferred' };
  }
  if (share === null) {
    await wipe();
    return { kind: 'refused' };
  }

  const shareBytes = decodeKey(share);
  if (!shareBytes) {
    await wipe();
    return { kind: 'unusable' };
  }

  const guardSecret = await answerGuard(record.guard, phrase);
  // A passkey that would not answer is a declined or absent authenticator, not a
  // dead record — the blob stays, and the button can be pressed again.
  if (!guardSecret) return { kind: 'deferred' };

  const sealed = await unlockCell(localKey, record.keys);
  if (!sealed) {
    await wipe();
    return { kind: 'unusable' };
  }

  const plain = open(wrapKey(shareBytes, guardSecret), keysAad(trustId), sealed);
  if (!plain) {
    // Under a phrase, a failure here is almost always a typo, and wiping would
    // turn one into a re-pairing. Hardware cannot mistype: its answer is either
    // the right one or the record is genuinely dead.
    if (record.guard.kind === 'passphrase') return { kind: 'wrong-passphrase' };
    await wipe();
    return { kind: 'unusable' };
  }

  try {
    const payload = JSON.parse(new TextDecoder().decode(plain)) as {
      dataKey?: string;
      grantSecret?: string;
    };
    const dataKey = decodeKey(payload.dataKey);
    const grantSecret = decodeKey(payload.grantSecret);
    if (!dataKey || !grantSecret) throw new Error('shape');
    return { kind: 'opened', session: { dataKey, grantSecret }, guard: record.guard.kind };
  } catch {
    await wipe();
    return { kind: 'unusable' };
  }
}

/**
 * Drops what is stored here and answers the trust id, so the caller can have
 * the backend destroy the share too. Local first: a browser that was asked to
 * forget must forget even if the network call never lands.
 */
export async function forgetThisBrowser(store: TrustStore = indexedDbTrustStore): Promise<string | null> {
  const record = await readRecord(store);
  const localKey = await store.get<CryptoKey>(WRAP_KEY);
  let trustId: string | null = null;
  if (record?.unlock && localKey) {
    const idBytes = await unlockCell(localKey, record.unlock);
    if (idBytes) trustId = new TextDecoder().decode(idBytes);
  }
  await store.clear();
  return trustId;
}

function decodeKey(value: string | undefined): Uint8Array | null {
  if (typeof value !== 'string') return null;
  try {
    const bytes = fromBase64(value);
    return bytes.length === KEY_BYTES ? bytes : null;
  } catch {
    return null;
  }
}
